import { type AvailabilityListing } from '@shared/types/availability';

export interface BuyableAvailability {
  isBuyable: boolean;
  purchasePrice: number | null;
  guestCount: number;
  listing?: AvailabilityListing;
}

export interface BestAvailableBuyableAvailability extends BuyableAvailability {
  isBestAvailable?: boolean;
}

/**
 * Returns the first non-null purchase price for a buyable availability.
 * Depends on availabilities being sorted by price (ascending).
 */
export const getLowestPurchasePrice = (
  availabilitiesSortedByPrice: BuyableAvailability[],
): BuyableAvailability['purchasePrice'] => {
  const availability = availabilitiesSortedByPrice.find(
    (a) => a.isBuyable && a.purchasePrice !== null,
  );

  if (!availability) return null;

  return availability.purchasePrice;
};

export const getBestAvailablePurchasePrice = (
  availabilities: BestAvailableBuyableAvailability[],
): BuyableAvailability['purchasePrice'] => {
  const bestAvailable = availabilities.find(
    (availability) =>
      availability.isBestAvailable &&
      availability.isBuyable &&
      availability.purchasePrice !== null,
  );

  return bestAvailable?.purchasePrice ?? null;
};

export const groupTimesByServiceWindow = (
  availabilitiesGroupedByTime: Record<string, { serviceWindow: string }[]>,
): [string, string[]][] => {
  const times = Object.keys(availabilitiesGroupedByTime);

  return times.reduce<[string, string[]][]>((groups, time) => {
    const serviceWindowForTime =
      availabilitiesGroupedByTime[time][0].serviceWindow;
    const existingGroup = groups.find(([sw]) => sw === serviceWindowForTime);

    return existingGroup
      ? groups.map(([sw, times]) =>
          sw === serviceWindowForTime ? [sw, [...times, time]] : [sw, times],
        )
      : [...groups, [serviceWindowForTime, [time]]];
  }, []);
};
